import {interval, Observable, Subscription} from 'rxjs';
import {share, tap} from 'rxjs/operators';
import {LibrarySymbolInfo, ResolutionString, SubscribeBarsCallback} from '../charting-library/datafeed-api';

import {getErrorMessage, logMessage} from './helpers';

import {GetBarsResult, HistoryProvider} from './history-provider';

interface DataSubscriber {
    symbolInfo: LibrarySymbolInfo;
    resolution: ResolutionString;
    lastBarTime: number | null;
    listener: SubscribeBarsCallback;
    subscription: Subscription;
}

export interface DataSubscribers {
    [guid: string]: DataSubscriber;
}

export class DataPulseProvider {

    protected interval$: Observable<number>;

    private readonly subscribers: DataSubscribers = {};
    private requestsPending: number = 0;
    private readonly historyProvider: HistoryProvider;

    public constructor(historyProvider: HistoryProvider, updateFrequency: number) {
        this.historyProvider = historyProvider;
        this.interval$ = interval(updateFrequency).pipe(
            tap(() => this.updateData()),
            share()
        );
    }

    public subscribeBars(
        symbolInfo: LibrarySymbolInfo,
        resolution: ResolutionString,
        newDataCallback: SubscribeBarsCallback,
        listenerGuid: string
    ): void {
        if (this.subscribers[listenerGuid]) {
            logMessage(`DataPulseProvider: already has subscriber with id=${listenerGuid}`);
            return;
        }

        this.subscribers[listenerGuid] = {
            lastBarTime: null,
            listener: newDataCallback,
            resolution,
            symbolInfo,
            subscription: this.interval$.subscribe()
        };

        logMessage(`DataPulseProvider: subscribed for #${listenerGuid} - {${symbolInfo.name}, ${resolution}}`);
    }

    public unsubscribeBars(listenerGuid: string): void {
        this.subscribers[listenerGuid].subscription.unsubscribe();
        delete this.subscribers[listenerGuid];
        logMessage(`DataPulseProvider: unsubscribed for #${listenerGuid}`);
    }

    protected updateData(): void {
        if (this.requestsPending > 0) {
            return;
        }

        this.requestsPending = 0;
        Object.keys(this.subscribers).forEach(listenerGuid => {
            this.requestsPending += 1;

            const message = `DataPulseProvider: data for #${listenerGuid} updated`;

            this.updateDataForSubscriber(listenerGuid)
                .then(() => {
                    this.requestsPending -= 1;
                    logMessage(`${message} successfully, pending=${this.requestsPending}`);
                })
                .catch((reason?: string | Error) => {
                    this.requestsPending -= 1;
                    logMessage(`${message} with error=${getErrorMessage(reason)}, pending=${this.requestsPending}`);
                });
        });
    }

    private updateDataForSubscriber(listenerGuid: string): Promise<void> {
        const subscriptionRecord = this.subscribers[listenerGuid];

        const rangeEndTime = parseInt((Date.now() / 1000).toString(), undefined);

        // BEWARE: please note we really need 2 bars, not the only last one
        // see the explanation below. `10` is the `large enough` value to work around holidays
        const rangeStartTime = rangeEndTime - periodLengthSeconds(subscriptionRecord.resolution, 10);

        return this.historyProvider.getBars(
            subscriptionRecord.symbolInfo,
            subscriptionRecord.resolution,
            rangeStartTime, rangeEndTime)
            .then((result: GetBarsResult) => {
                this.onSubscriberDataReceived(listenerGuid, result);
            });
    }

    private onSubscriberDataReceived(listenerGuid: string, result: GetBarsResult): void {
        // means the subscription was cancelled while waiting for data
        if (!this.subscribers[listenerGuid]) {
            logMessage(`DataPulseProvider: Data comes for already unsubscribed subscription #${listenerGuid}`);
            return;
        }

        const bars = result.bars;
        if (bars.length === 0) {
            return;
        }

        const lastBar = bars[bars.length - 1];
        const subscriptionRecord = this.subscribers[listenerGuid];

        if (subscriptionRecord.lastBarTime !== null && lastBar.time < subscriptionRecord.lastBarTime) {
            return;
        }

        const isNewBar = subscriptionRecord.lastBarTime !== null && lastBar.time > subscriptionRecord.lastBarTime;

        // Pulse updating may miss some trades data (ie, if pulse period = 10 seconds
        // and new bar is started 5 seconds later after the last update, the
        // old bar's last 5 seconds trades will be lost).
        // Thus, at fist we should broadcast old bar updates when it's ready.
        if (isNewBar) {
            if (bars.length < 2) {
                throw new Error('Not enough bars in history for proper pulse update. Need at least 2.');
            }

            const previousBar = bars[bars.length - 2];
            subscriptionRecord.listener(previousBar);
        }

        subscriptionRecord.lastBarTime = lastBar.time;
        subscriptionRecord.listener(lastBar);
    }
}

function periodLengthSeconds(resolution: string, requiredPeriodsCount: number): number {
    let daysCount = 0;

    if (resolution === 'D') {
        daysCount = requiredPeriodsCount;
    } else if (resolution === 'M') {
        daysCount = 31 * requiredPeriodsCount;
    } else if (resolution === 'W') {
        daysCount = 7 * requiredPeriodsCount;
    } else {
        daysCount = requiredPeriodsCount * parseInt(resolution, undefined) / (24 * 60);
    }

    return daysCount * 24 * 60 * 60;
}
